// Small server-paginated table for legacy `table()` endpoints ({ data, count }),
// used by the item trails and gift card logs pages.
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Alert, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, TableSortLabel, Typography } from '@mui/material';

import { adminApi, apiErrorMessage } from '../../api/client';
import { ensureLocation, onLocationChanged } from '../../session/location';
import { t } from '../../i18n';
import { UI } from '../../brand/ui';

export type RemoteColumn<T> = {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
};

export function RemoteTable<T extends Record<string, unknown>>({
  endpoint,
  columns,
  orderBy = 'created_at desc',
  params,
  requiresLocation,
  rowTone,
  footer,
  refreshKey
}: {
  endpoint: string;
  columns: RemoteColumn<T>[];
  orderBy?: string;
  params?: Record<string, string>;
  requiresLocation?: boolean;
  rowTone?: (row: T) => 'success' | 'warning' | undefined;
  footer?: (rows: T[]) => ReactNode;
  refreshKey?: unknown;
}) {
  const [rows, setRows] = useState<T[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState(orderBy);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const paramsKey = JSON.stringify(params ?? {});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (requiresLocation) await ensureLocation();
      const response = await adminApi.get(endpoint, { params: { ...(params ?? {}), page: page + 1, limit, orderBy: sort } });
      setRows(Array.isArray(response.data?.data) ? response.data.data : []);
      setCount(Number(response.data?.count ?? 0));
    } catch (err) {
      setError(apiErrorMessage(err, t('common.unableToLoad', { name: '' })));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, paramsKey, page, limit, sort, requiresLocation]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  useEffect(() => (requiresLocation ? onLocationChanged(() => void load()) : undefined), [load, requiresLocation]);

  const [sortKey, sortDir] = sort.split(' ');

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {loading ? <LinearProgress /> : null}
      {error ? <Alert severity="error" sx={{ m: 2 }}>{error}</Alert> : null}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} align={column.align}>
                  {column.sortable ? (
                    <TableSortLabel
                      active={sortKey === column.key}
                      direction={sortKey === column.key && sortDir === 'desc' ? 'desc' : 'asc'}
                      onClick={() => {
                        setSort(sortKey === column.key && sortDir !== 'desc' ? `${column.key} desc` : `${column.key} asc`);
                        setPage(0);
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && !rows.length ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>{t('catalog.noRecords')}</Typography>
                </TableCell>
              </TableRow>
            ) : null}
            {rows.map((row, index) => {
              const tone = rowTone?.(row);
              return (
                <TableRow key={String(row.id ?? index)} sx={tone ? { bgcolor: tone === 'success' ? UI.successTint : UI.warningTint } : undefined}>
                  {columns.map((column) => (
                    <TableCell key={column.key} align={column.align}>
                      {column.render ? column.render(row) : String(row[column.key] ?? '')}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
            {footer && rows.length ? footer(rows) : null}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                count={count}
                page={page}
                rowsPerPage={limit}
                rowsPerPageOptions={[10, 25, 50, 100]}
                onPageChange={(_event, next) => setPage(next)}
                onRowsPerPageChange={(event) => {
                  setLimit(Number(event.target.value));
                  setPage(0);
                }}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Paper>
  );
}
